import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import fixRotation from 'fix-image-rotation';
import {CreateUpdateEntityPortlet} from '@ozgene/gp5.components.create-update-entity-portlet';
import StepEditor from 'StepUpdateCreate/Components/StepEditor';
import {
  saveStep,
  findStepById,
  saveAllStepConsumables,
  deleteStepConsumable,
  saveStepMedia,
} from 'StepUpdateCreate/service/StepService';

export default function CreateUpdateStepPortlet(props) {
  const {stepId, sbs, onCancel, onSave} = props;
  const [step, setStep] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const handleSave = async (entity) => {
    let savedStep;
    const {stepConsumableUnits: newConsumables = [], media} = entity;

    if (!media?.id) {
      // Deconstruction above saves media independently so delete doesn't affect it, just removes it from entity
      delete entity.media;
    }

    //Save step
    try {
      savedStep = await saveStep(entity, sbs);
    } catch (error) {
      // TODO: implement better alert system
      // eslint-disable-next-line no-alert
      alert('Failed to upload step.');

      return;
    }
    //Save media
    if (!!media) {
      const {id: mediaId} = media;

      if (!mediaId) {
        let image = media;

        if (media.type.includes('image')) {
          image = await fixRotation.fixRotation(media);
        }
        try {
          const savedMedia = await saveStepMedia(savedStep.id, image);

          savedStep.media = savedMedia;
        } catch (error) {
          // TODO: implement better alert system
          // eslint-disable-next-line no-alert
          alert('Failed to upload media.');
        }
      }
    }
    const originalConsumableUnits = step ? step.stepConsumableUnits : [];
    const {id: savedStepId} = savedStep;

    //Save step consumables
    for (const consumable of newConsumables) {
      const index = originalConsumableUnits.findIndex(
        ({id}) => id === consumable.id,
      );

      if (index >= 0 && originalConsumableUnits[index] !== consumable) {
        consumable.stepConsumables = {id: savedStepId};
        await saveAllStepConsumables([consumable]);
      }
    }

    if (newConsumables.length !== originalConsumableUnits.length) {
      // Delete all old consumables that arent in the new consumabkle
      for (const consumable of originalConsumableUnits) {
        if (!newConsumables.some((c) => c.id === consumable.id)) {
          await deleteStepConsumable(consumable);
        }
      }
    }

    onSave(savedStepId);
  };

  useEffect(() => {
    const handleGetStep = async () => {
      if (stepId) {
        const newStep = await findStepById(stepId);

        setStep(newStep);
      }
      setIsLoading(false);
    };

    if (isLoading) {
      handleGetStep();
    }
  }, [isLoading, stepId]);

  return (
    !isLoading && (
      <CreateUpdateEntityPortlet
        EditorComponent={StepEditor}
        entity={step}
        onSave={handleSave}
        onCancel={onCancel}
        entityType="Step"
        getEntityLabel={() => 'Step'}
        colour="#c29873"
      />
    )
  );
}

CreateUpdateStepPortlet.propTypes = {
  stepId: PropTypes.string,
  sbs: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  step: PropTypes.shape({
    id: PropTypes.string,
    stepType: PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
    }),
    stepLevel: PropTypes.shape({
      id: PropTypes.string,
      level: PropTypes.string,
    }),
    hh: PropTypes.number,
    mm: PropTypes.number,
    ss: PropTypes.number,
    subSbs: PropTypes.shape({
      id: PropTypes.string,
    }),
    instruction: PropTypes.string,
    detail: PropTypes.string,
    badges: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        src: PropTypes.string,
      }),
    ),
    stepConsumables: PropTypes.arrayOf(
      PropTypes.shape({
        consumable: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        metricUnit: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        quantity: PropTypes.number,
      }),
    ),
    media: PropTypes.object,
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

CreateUpdateStepPortlet.defaultProps = {
  step: null,
  stepId: '',
};
